import React, { useContext } from 'react';
import RecommendationDetails from '../../recommendationGenericComponents/recommendationDetails';
import {
  BASE_PROPERTIES,
  COST_PROPERTIES,
  EXTENDED_SUPPORT_PROPERTIES,
  OPTIONS_PROPERTIES,
  RDS_PROPERTIES,
} from '../../recommendationPropertyUtils';
import RecommendationDetailsLayout from '../../recommendationDetailsLayout';
import RecommendationPreferences from '../../recommendationGenericComponents/recommendationPreferences';
import RecommendationInsideLook from '../../recommendationGenericComponents/recommendationInsideLook';
import OtherResourceIdRecommendations from '../../recommendationGenericComponents/otherResourceIdRecommendations';
import RecommendationOptionsPanel from '../../recommendationGenericComponents/recommendationOptionsPanel';
import RecommendationCommand, { COMMAND_TYPES } from '../../recommendationGenericComponents/recommendationCommand';
import RecommendationOptionsContext from '../../recommendationGenericComponents/recommendationOptionsContext';

const OPEN_SEARCH_EXTENDED_SUPPORT_PROPERTIES = {
  EBS_ENABLED: {
    label: 'EBS Enabled',
    getterFunction: (recommendation) => (recommendation.recData?.ebs_enabled ? 'Yes' : 'No'),
  },
  VOLUME_SIZE: {
    label: 'Volume Size',
    getterFunction: (recommendation) => recommendation.recData?.volume_size,
  },
  VOLUME_TYPE: {
    label: 'Volume Type',
    getterFunction: (recommendation) => recommendation.recData?.volume_type,
  },
};

const OpenSearchExtendedSupport = ({ recommendation }) => {
  const { selectedOptionIndex } = useContext(RecommendationOptionsContext);

  const properties = {
    ...BASE_PROPERTIES,
    ...RDS_PROPERTIES,
    ...COST_PROPERTIES,
    ...OPEN_SEARCH_EXTENDED_SUPPORT_PROPERTIES,
    ...EXTENDED_SUPPORT_PROPERTIES,
  };

  const description = `AWS OpenSearch Extended Support allows you to continue running your OpenSearch domains on a major engine version past the end of 
    standard support for an additional cost. We recommend upgrading to a version within standard support to benefit from continued security updates,
    performance improvements, and avoid extra charges.`;

  const insideLookComponent = <RecommendationInsideLook description={description} />;

  const otherResourceIdRecommendations = <OtherResourceIdRecommendations resourceId={recommendation?.resourceId} />;

  const detailsComponent = (
    <RecommendationDetails recommendationProperties={properties} recommendation={recommendation} />
  );

  const preferencesComponent = <RecommendationPreferences recommendationType={recommendation.typeId} />;

  const CURRENT_PROPERTIES = [
    properties.VERSION,
    properties.EXTENDED_SUPPORT_TYPE,
    properties.START_OF_EXTENDED_SUPPORT_DATE,
    properties.END_OF_EXTENDED_SUPPORT_DATE,
    properties.CURRENT_ANNUAL_COST,
    properties.CURRENT_INSTANCE_TYPE,
    properties.EBS_ENABLED,
    properties.VOLUME_SIZE,
    properties.VOLUME_TYPE,
  ];

  const ALTERNATIVE_PROPERTIES = [
    properties.RECOMMENDED_VERSION,
    properties.RECOMMENDED_SUPPORT_TYPE,
    properties.RECOMMENDED_END_OF_EXTENDED_SUPPORT_DATE,
    properties.RECOMMENDED_ANNUAL_COST,
    OPTIONS_PROPERTIES.POTENTIAL_SAVINGS,
  ];

  const alternatives = recommendation?.recData?.alternatives;
  const optionsPanelComponent =
    Array.isArray(alternatives) && alternatives.length ? (
      <RecommendationOptionsPanel
        alternatives={alternatives}
        currentProperties={CURRENT_PROPERTIES}
        alternativeProperties={ALTERNATIVE_PROPERTIES}
        noticeComment={
          properties.START_OF_EXTENDED_SUPPORT_DATE.getterFunction(recommendation) > new Date()
            ? 'Early notice for extended support!'
            : ''
        }
        recommendation={recommendation}
        isRecommendedOption
      />
    ) : null;

  const commandsList = [
    {
      type: COMMAND_TYPES.CLI,
      instructions: [
        { actionText: '', actionCommand: recommendation?.recData?.alternatives[selectedOptionIndex]?.command },
      ],
    },
  ];

  const commandComponent = <RecommendationCommand commandsList={commandsList} />;

  return (
    <RecommendationDetailsLayout
      command={commandComponent}
      insideLook={insideLookComponent}
      otherResourceIdRecommendations={otherResourceIdRecommendations}
      details={detailsComponent}
      optionsPanel={optionsPanelComponent}
      preferences={preferencesComponent}
    />
  );
};

export default OpenSearchExtendedSupport;
